import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Sprint H · Batch 6 — Hook que vuelve un elemento "arrastrable" libremente
 * por la pantalla (mouse + touch). Persiste posición en localStorage.
 *
 * Uso:
 *   const { ref, dragProps, dragHandleProps, style } = useDraggable({
 *     storageKey: "floating-chat-pos",
 *     defaultPos: { right: 16, bottom: 16 },
 *   });
 *   return <div ref={ref} style={style} {...dragProps}><span {...dragHandleProps}>≡</span>...</div>
 *
 * Comportamiento:
 *  - `dragProps` se aplica al contenedor principal; controla cursor durante drag.
 *  - `dragHandleProps` se aplica a la zona que inicia el drag (header del panel
 *    o el propio botón cuando está colapsado).
 *  - Limita la posición a viewport para que no se pierda fuera de pantalla.
 *  - Persiste en localStorage para que el usuario no tenga que reubicarlo cada vez.
 */
export function useDraggable({ storageKey, defaultPos = { right: 16, bottom: 16 } }) {
  const ref = useRef(null);
  const dragStateRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (saved && typeof saved.left === "number" && typeof saved.top === "number") {
        return saved;
      }
    } catch (e) { /* ignore */ }
    return null;
  });

  const clampToViewport = useCallback((p) => {
    if (typeof window === "undefined" || !ref.current) return p;
    const el = ref.current;
    const w = el.offsetWidth || 60;
    const h = el.offsetHeight || 60;
    const vw = window.innerWidth, vh = window.innerHeight;
    return {
      left: Math.max(8, Math.min(p.left, vw - w - 8)),
      top: Math.max(8, Math.min(p.top, vh - h - 8)),
    };
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!dragStateRef.current.dragging) return;
    e.preventDefault?.();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const next = clampToViewport({
      left: clientX - dragStateRef.current.offsetX,
      top:  clientY - dragStateRef.current.offsetY,
    });
    setPos(next);
  }, [clampToViewport]);

  const endDrag = useCallback(() => {
    if (!dragStateRef.current.dragging) return;
    dragStateRef.current.dragging = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    // Persistir
    setPos((current) => {
      if (current && storageKey) {
        try { localStorage.setItem(storageKey, JSON.stringify(current)); } catch (e) {}
      }
      return current;
    });
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchend", endDrag);
  }, [onPointerMove, storageKey]);

  const startDrag = useCallback((e) => {
    if (!ref.current) return;
    // Solo botón izquierdo del ratón
    if (e.button !== undefined && e.button !== 0) return;
    const rect = ref.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStateRef.current = {
      dragging: true,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    window.addEventListener("mousemove", onPointerMove, { passive: false });
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchend", endDrag);
  }, [onPointerMove, endDrag]);

  useEffect(() => () => endDrag(), [endDrag]);

  // Reajusta al redimensionar viewport
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => p ? clampToViewport(p) : p);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos, clampToViewport]);

  const style = pos
    ? { position: "fixed", left: pos.left, top: pos.top, right: "auto", bottom: "auto", zIndex: 9997 }
    : { position: "fixed", right: defaultPos.right, bottom: defaultPos.bottom, zIndex: 9997 };

  const dragHandleProps = {
    onMouseDown: startDrag,
    onTouchStart: startDrag,
    style: { cursor: "grab", touchAction: "none" },
  };

  return { ref, dragHandleProps, style };
}
